import React, { useEffect, useState } from 'react';
import {
  Button,
  Typography,
  TextField,
  MenuItem,
  Checkbox,
  Chip,
  ListItemText,
  IconButton,
  Tooltip,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import DeleteIcon from '@material-ui/icons/Delete';
import { Formik } from 'formik';
import { useHistory } from 'react-router-dom';
import * as Yup from 'yup';
import { toJS } from 'mobx';
import { InviteSchema } from '../../../validation';
import { useRootStore } from '../../../stores/BridgeProvider';
import { StyledForm, FormRow, ErrorText, RowItem } from '../../styles/styles';
import Spinner from '../../util/Spinner';
import { MenuList, filterOptions } from '../../util/ReactSelectOverrides';
import UnauthorizedSnackbar from '../../common/UnauthorizedSnackbar';
import SimpleSelect from '../../util/SimpleSelect';
import ConfirmDialog from '../../dealMapper/common/ConfirmDialog';
import { safeFalsy, safeEval } from '../../util/UtilFunctions';
import { CustomCreatableSelect } from '../../util/CustomCreatableSelect';

const useStyles = makeStyles(theme => ({
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  chip: {
    margin: 2,
  },
}));

// const InviteSchema = Yup.object().shape({
//   email: Yup.string()
//     .email('Invalid Email')
//     .required('Required'),
//   roles: Yup.array().min(1),
//   contactId: Yup.string().required('Please select or create a name'),
// });

const InviteForm = ({ handleSubmission, initialValues = {}, inviteId }) => {
  const history = useHistory();
  const { authStore, dataStore, orgStore } = useRootStore();
  const classes = useStyles();
  const [isLoading, setLoading] = useState(true);
  const [isCreating, setCreating] = useState(false);
  const [userRoles, setUserRoles] = useState([]);
  const [authorized, setAuthorized] = useState(true);
  const { contacts } = dataStore;
  useEffect(() => {
    const getRoles = async () => {
      try {
        const { data } = await authStore.request.get(
          `${process.env.REACT_APP_API}/api/roles`,
        );
        setUserRoles(data.filter(role => !role.IsOwner));
      } catch (e) {
        const { status } = e?.response;
        if (status === 403) setAuthorized(false);
      }
    };

    const initialize = async () => {
      const requests = [getRoles()];
      if (contacts.length === 0) {
        requests.push(dataStore.retrieveContacts());
      }
      await Promise.all(requests);
      setLoading(false);
    };
    initialize();
  }, []);
  if (!authorized)
    return (
      <UnauthorizedSnackbar
        message="Unauthorized"
        closeLink="/profile/settings"
      />
    );

  const cancelInvite = async () => {
    try {
      await authStore.request.delete(
        `${process.env.REACT_APP_API}/api/invitation`,
        {
          data: { invitationId: inviteId },
        },
      );
      history.push('/user-management/pending-invites');
    } catch (e) {
      console.error(e);
    }
  };
  if (isLoading) return <Spinner />;
  return (
    <Formik
      initialValues={{
        email: safeFalsy(initialValues.email),
        roles: safeFalsy(initialValues.roles, []),
        contactId: safeFalsy(initialValues.contactId),
        contact: dataStore.getContactFromId(initialValues.contactId),
      }}
      validationSchema={InviteSchema}
      onSubmit={async (values, { resetForm, setSubmitting }) => {
        try {
          await handleSubmission(values);
          resetForm();

          history.push('/user-management/pending-invites');
        } catch (e) {
          console.error(e);
          setSubmitting(false);
        }
      }}
      render={({
        handleBlur,
        handleChange,
        errors,
        touched,
        handleSubmit,
        isSubmitting,
        isValid,
        setFieldValue,
        values,
      }) => {
        const showError = val => errors[val] && touched[val];
        const handleCreateContact = async inputValue => {
          setCreating(true);
          try {
            const { data: contactData } = await authStore.request.post(
              `${process.env.REACT_APP_API}/api/contact`,
              {
                Name: inputValue,
                IsAgent: 0,
              },
            );
            const { id: cId } = contactData;
            await dataStore.retrieveContacts();
            const contact = dataStore.getContactFromId(cId);
            setFieldValue('contact', contact);
            setFieldValue('contactId', cId);
          } catch (e) {
            console.error(e);
          }
          setCreating(false);
        };

        const handleContactValChange = val => {
          setFieldValue('contact', val);
          setFieldValue(
            'contactId',
            safeEval(() => val.ContactId),
            null,
          );
          if (!inviteId && val) {
            const { Email = '' } = val;
            if (Email && Email.length > 0) {
              setFieldValue('email', Email);
            }
          }
        };

        return (
          <div
            style={{
              display: 'flex',
              flexFlow: 'column nowrap',
            }}
          >
            {inviteId ? (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="h4" style={{ margin: '0 auto' }}>
                  Modify Invitation
                </Typography>
                <ConfirmDialog
                  title="Cancel Invitation?"
                  actions={({ handleClose }) => (
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-around',
                        flex: 1,
                      }}
                    >
                      <Button
                        color="primary"
                        variant="contained"
                        onClick={() => {
                          cancelInvite();
                          handleClose();
                        }}
                      >
                        Yes
                      </Button>
                      <Button
                        color="secondary"
                        variant="contained"
                        onClick={() => {
                          handleClose();
                        }}
                      >
                        No
                      </Button>
                    </div>
                  )}
                >
                  {({ handleOpen }) => {
                    return (
                      <Tooltip title="Cancel Invitation" placement="top" arrow>
                        <DeleteIcon
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleOpen()}
                          fontSize="small"
                        />
                      </Tooltip>
                    );
                  }}
                </ConfirmDialog>
              </div>
            ) : (
              <Typography variant="h4">Invite User</Typography>
            )}
            <StyledForm onSubmit={handleSubmit}>
              <div style={{ width: '100%', marginTop: '1.5rem' }}>
                <CustomCreatableSelect
                  isSearchable
                  isClearable
                  placeholder="Name"
                  isRequired
                  components={{ MenuList }}
                  options={contacts}
                  getOptionLabel={option => option.Name}
                  getOptionValue={option => option.ContactId}
                  isValidNewOption={(inputValue, x, selectOptions) =>
                    selectOptions.findIndex(
                      option => option.Name === inputValue,
                    ) < 0
                  }
                  formatCreateLabel={inputValue => `Create "${inputValue}"`}
                  getNewOptionData={(inputValue, optionLabel) => ({
                    Name: optionLabel,
                    ContactId: inputValue,
                    __isNew__: true,
                  })}
                  value={values.contact}
                  filterOption={filterOptions}
                  isDisabled={isCreating}
                  isLoading={isCreating}
                  onCreateOption={handleCreateContact}
                  onChange={handleContactValChange}
                />
              </div>
              {showError('contactId') && (
                <ErrorText>{errors.contactId}</ErrorText>
              )}
              <FormRow>
                <RowItem>
                  <TextField
                    fullWidth
                    name="email"
                    label={
                      <div>
                        User Email <span style={{ color: 'red' }}>*</span>
                      </div>
                    }
                    value={values.email}
                    margin="normal"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    variant="outlined"
                    // error={showError('email')}
                    disabled={!!inviteId}
                  />
                  {showError('email') && <ErrorText>{errors.email}</ErrorText>}
                </RowItem>
              </FormRow>
              <FormRow>
                <SimpleSelect
                  selectProps={{
                    required: true,
                    multiple: true,
                    renderValue: selected => (
                      <div className={classes.chips}>
                        {selected.map(value => (
                          <Chip
                            key={value}
                            label={
                              userRoles.find(role => role.OrgRoleId === value)
                                .Name
                            }
                            className={classes.chip}
                          />
                        ))}
                      </div>
                    ),
                  }}
                  error={showError('roles')}
                  label="Roles"
                  name="roles"
                  isRequired
                  value={values.roles}
                  handleChange={e => setFieldValue('roles', e.target.value)}
                  inputProps={{
                    name: 'roles',
                    id: 'outlined-roles-simple',
                  }}
                >
                  {userRoles.map(({ Name, OrgRoleId }) => (
                    <MenuItem value={OrgRoleId} key={OrgRoleId}>
                      <Checkbox
                        checked={values.roles.indexOf(OrgRoleId) > -1}
                      />
                      <ListItemText primary={Name} />
                    </MenuItem>
                  ))}
                </SimpleSelect>
              </FormRow>
              <div
                style={{
                  display: 'flex',
                  width: '100%',
                  flexFlow: 'nowrap',
                  justifyContent: 'space-evenly',
                  marginTop: '15px',
                }}
              >
                <Button
                  color="primary"
                  variant="contained"
                  type="submit"
                  size="large"
                  disabled={!isValid || isSubmitting}
                >
                  Submit
                </Button>
                {inviteId && (
                  <Button
                    color="secondary"
                    variant="contained"
                    size="large"
                    onClick={() => history.goBack()}
                  >
                    Cancel
                  </Button>
                )}
              </div>
            </StyledForm>
          </div>
        );
      }}
    />
  );
};

export default InviteForm;
